import React, { useState, useEffect, useRef } from 'react';
import { lockScroll, unlockScroll } from '../../utils/scrollbarUtils';
import { authHeaders } from '../../utils/api';
import adminHamburgerIcon from '../../assets/icons/admin/hamburger_icon.png';
import adminDeleteIcon from '../../assets/icons/admin/delete.png';
import languageEnIcon from '../../assets/icons/language/en.png';
import languageCnIcon from '../../assets/icons/language/cn.png';
import languageJaIcon from '../../assets/icons/language/ja.png';
import bottomNavBg from '../../assets/icons/common/bottomNav.png';

const CategoryEditBottomSheet = ({ isOpen, onClose, onShowToast, selectedLanguage = 'ko', categories = [], categoryCodes = {}, categoryTranslations = {}, onReorder, onUpdateCategoryTranslation, mode = 'pos', storeId, posType = '', onCustomChange }) => {
  const isCustom = mode === 'custom';
  // WowPos 매장은 wowpos_categories 테이블 사용 (mode=custom 이지만 데이터 소스 다름)
  const isWowpos = posType === 'wowpos';
  const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // 저장 중 상태
  const [dragStartY, setDragStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [scrollbarTop, setScrollbarTop] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [isScrollbarDragging, setIsScrollbarDragging] = useState(false);
  const bottomSheetRef = useRef(null);
  const wrapperRef = useRef(null);
  const formContainerRef = useRef(null);
  const scrollbarRef = useRef(null);
  
  // Menu list states
  const [items, setItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [itemDragStartY, setItemDragStartY] = useState(0);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [originalIndex, setOriginalIndex] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [isNewItem, setIsNewItem] = useState(false);
  const [invalidItems, setInvalidItems] = useState([]); // 빈 필드 추적
  const [originalItems, setOriginalItems] = useState([]); // 원본 아이템 저장
  const itemRefs = useRef([]);
  const autoScrollRef = useRef(null);
  
  // 팝업 모달 상태
  const [showAddModal, setShowAddModal] = useState(false);
  const [, setAddModalAnimating] = useState(false); // eslint-disable-line no-unused-vars
  const [, setNewCategoryName] = useState(''); // eslint-disable-line no-unused-vars
  const [, setInputError] = useState(false); // eslint-disable-line no-unused-vars
  const [, setInputFocused] = useState(false); // eslint-disable-line no-unused-vars
  const [, setAnimationKey] = useState(0); // eslint-disable-line no-unused-vars
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const initialScrollTop = useRef(0);
  const accumulatedScroll = useRef(0);
  const editInputRef = useRef(null);

  // === 모드2: 커스텀 카테고리 ===
  // 언어별 이름 컬럼 (ko는 name, 나머지는 name_<lang>)
  const langNameCol = selectedLanguage === 'ko' ? 'name' : `name_${selectedLanguage}`;

  // 커스텀 카테고리 목록 → items 형태로 로드
  const reloadCustomItems = async () => {
    if (!storeId) return;
    try {
      const url = isWowpos
        ? `${API_BASE_URL}/api/wowpos/categories/${storeId}`
        : `${API_BASE_URL}/api/stores/${storeId}/custom-categories`;
      const d = await fetch(url, { headers: authHeaders() }).then(r => r.json());
      if (!d.success) return;
      const list = (d.categories || []).map(c => {
        const koName = isWowpos ? c.name_ko : c.name;
        return {
          id: `cat-${c.id}`,
          cid: c.id,
          name: (selectedLanguage === 'ko' ? koName : c[`name_${selectedLanguage}`]) || koName,
          originalKey: koName,
          isNew: false,
          is_hidden: isWowpos ? c.is_hidden : !c.is_visible
        };
      });
      setItems(list);
      setOriginalItems(list);
      setInvalidItems([]);
    } catch (e) {
      console.error('[커스텀 카테고리] 로드 실패:', e);
    }
  };

  // 카테고리 추가 — 로컬 items에만 반영 + 즉시 편집 모드 진입 (이름 입력 대기)
  const handleCustomAddCategory = () => {
    const tempId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setItems(prev => [...prev, {
      id: tempId,
      cid: null,            // 새 항목은 cid 없음 → 저장 시 POST
      name: '',
      originalKey: '',
      isNew: true,
      is_hidden: false
    }]);
    setEditingItemId(tempId);
    setEditingText('');
    setIsNewItem(true);
    setTimeout(() => {
      if (formContainerRef.current) {
        formContainerRef.current.scrollTop = formContainerRef.current.scrollHeight;
      }
      // 새로 렌더된 마지막 input에 포커스
      const inputs = formContainerRef.current?.querySelectorAll('.category-edit-input');
      if (inputs && inputs.length > 0) {
        inputs[inputs.length - 1].focus();
      }
    }, 80);
  };

  // 카테고리 삭제 — confirm 없이 애니메이션 후 로컬 items에서 제거
  // (실제 DB 삭제는 저장 버튼 시 한 번에)
  const handleCustomDeleteCategory = (cid, itemId) => {
    if (deletingItemId) return;
    setDeletingItemId(itemId);
    setTimeout(() => {
      setItems(prev => prev.filter(it => it.id !== itemId));
      setDeletingItemId(null);
    }, 300);
  };

  // 카테고리 이름 변경 — 로컬 items만 수정 (저장 버튼 시 일괄 PUT)
  const handleCustomRename = (cidOrId, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed) return;
    setItems(prev => prev.map(it => {
      if (it.cid === cidOrId || it.id === cidOrId) {
        return { ...it, name: trimmed };
      }
      return it;
    }));
  };

  // 노출/숨김 토글 (현재 모드2에서는 노출 안 함 — 보존만)
  const handleCustomToggleVisible = async (cid, nextVisible) => {
    try {
      const url = isWowpos
        ? `${API_BASE_URL}/api/wowpos/categories/${cid}`
        : `${API_BASE_URL}/api/custom-categories/${cid}`;
      const body = isWowpos
        ? { is_hidden: !nextVisible }
        : { is_visible: nextVisible };
      await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body)
      });
      if (onCustomChange) onCustomChange();
    } catch (e) {
      console.error('[커스텀 카테고리] 노출 토글 실패:', e);
    }
  };

  // 저장: 원본 대비 추가/삭제/이름변경/순서를 일괄 반영
  const handleCustomSave = async () => {
    if (!storeId) return;
    const toRemove = originalItems.filter(o => !items.some(c => c.cid === o.cid));
    const toRename = items.filter(c => {
      if (!c.cid) return false;
      const orig = originalItems.find(o => o.cid === c.cid);
      return orig && orig.name !== c.name;
    });
    try {
      // 1) 삭제
      for (const it of toRemove) {
        const delUrl = isWowpos
          ? `${API_BASE_URL}/api/wowpos/categories/${it.cid}`
          : `${API_BASE_URL}/api/custom-categories/${it.cid}`;
        await fetch(delUrl, { method: 'DELETE', headers: authHeaders() });
      }
      // 2) 추가 (새 항목 POST → cid 받음) + 기존 cid 그대로 사용해서 순서 배열 구성
      const orderedCids = [];
      for (const it of items) {
        if (it.cid) {
          orderedCids.push(it.cid);
        } else {
          let createdId = null;
          if (isWowpos) {
            const r = await fetch(`${API_BASE_URL}/api/wowpos/categories`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ storeId: Number(storeId), name_ko: it.name })
            });
            const d = await r.json();
            if (d.success && d.category?.id) createdId = d.category.id;
          } else {
            const r = await fetch(`${API_BASE_URL}/api/stores/${storeId}/custom-categories`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ name: it.name, name_ko: it.name })
            });
            const d = await r.json();
            if (d.success && d.id) createdId = d.id;
          }
          if (createdId) orderedCids.push(createdId);
        }
      }
      // 3) 이름 변경
      for (const it of toRename) {
        if (isWowpos) {
          const body = selectedLanguage === 'ko'
            ? { name_ko: it.name }
            : { [`name_${selectedLanguage}`]: it.name };
          await fetch(`${API_BASE_URL}/api/wowpos/categories/${it.cid}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body)
          });
        } else {
          const body = selectedLanguage === 'ko'
            ? { name: it.name, name_ko: it.name }
            : { [langNameCol]: it.name };
          await fetch(`${API_BASE_URL}/api/custom-categories/${it.cid}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body)
          });
        }
      }
      // 4) 순서 일괄 저장
      if (isWowpos) {
        // wowpos 는 reorder endpoint 없음 — 각 카테고리 display_order 개별 PUT
        for (let i = 0; i < orderedCids.length; i++) {
          await fetch(`${API_BASE_URL}/api/wowpos/categories/${orderedCids[i]}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ display_order: i })
          });
        }
      } else {
        await fetch(`${API_BASE_URL}/api/stores/${storeId}/custom-categories/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ order: orderedCids })
        });
      }
      if (onCustomChange) onCustomChange();
    } catch (e) {
      console.error('[커스텀 카테고리] 저장 실패:', e);
    }
  };

  // Handle drag to close handlers
  const handleStart = (clientY) => {
    setIsDragging(true);
    setDragStartY(clientY);
    setCurrentY(0);
  };

  const handleMove = (clientY) => {
    if (!isDragging) return;
    const deltaY = clientY - dragStartY;
    if (deltaY > 0) { // 아래로만 드래그 가능
      setCurrentY(deltaY);
    }
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // 화면 높이의 20% 이상 또는 150px 이상 드래그하면 닫기
    const threshold = Math.min(window.innerHeight * 0.2, 150);
    if (currentY > threshold) {
      // 부드럽게 닫기 애니메이션
      setCurrentY(window.innerHeight);
      setTimeout(() => {
        onClose();
        setCurrentY(0);
      }, 200);
    } else {
      // 원위치로 돌아가기
      setCurrentY(0);
    }
  };

  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientY);
  };
  
  const handleMouseDown = (e) => handleStart(e.clientY);

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMove(e.clientY);
      const handleGlobalMouseUp = () => handleEnd();
      const handleGlobalTouchMove = (e) => handleMove(e.touches[0].clientY);
      const handleGlobalTouchEnd = () => handleEnd();

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      window.addEventListener('touchend', handleGlobalTouchEnd, { passive: false });
      
      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
        window.removeEventListener('touchmove', handleGlobalTouchMove);
        window.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isDragging, dragStartY, currentY]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // items 업데이트 - isOpen이 true가 될 때만 초기화
  useEffect(() => {
    if (isOpen) {
      // 모드2: 커스텀 카테고리는 별도 API에서 로드
      if (isCustom) {
        reloadCustomItems();
        return;
      }
      console.log('=== 카테고리 편집 시트 열림 ===');
      console.log('categories:', categories);
      console.log('categoryTranslations:', categoryTranslations);
      console.log('selectedLanguage:', selectedLanguage);

      const categoryItems = categories.filter(category => !(category.includes('네이버') || category.includes('리뷰'))).map((category, index) => {
        const categoryCode = categoryCodes[category]; // 카테고리명으로 코드 찾기
        const item = {
          id: `category-${index}`,
          name: (categoryCode && categoryTranslations[categoryCode]?.[selectedLanguage]) || category,
          originalKey: category, // 원본 카테고리명 저장
          isNew: false, // 기존 카테고리는 isNew false
          is_hidden: categoryCodes[`${category}_hidden`] || false
        };
        console.log(`카테고리 아이템 생성 [${index}]:`, item);
        console.log(`- 카테고리 코드: ${categoryCode}`);
        return item;
      });
      setItems(categoryItems);
      setOriginalItems(categoryItems); // 원본 저장
      setInvalidItems([]); // 초기화 시 에러 초기화
    }
  }, [isOpen, categories, categoryTranslations, selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(false);
      setIsVisible(true);
      
      // body 스크롤 방지
      lockScroll();
      
      window.history.pushState({ categoryEdit: true }, '');
      
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      setIsAnimating(false);
      
      setTimeout(() => {
        setIsVisible(false);
        // Reset states
        setEditingItemId(null);
        setEditingText('');
        setInvalidItems([]);
        // 애니메이션이 완료된 후 body 스크롤 복원
        unlockScroll();
      }, 200);
    }
  }, [isOpen]);

  // 페이지 뒤로가기 처리
  // onClose / showAddModal을 ref로 우회 → parent re-render churn에도 listener 1회만 등록
  // cleanup 시 우리가 push한 history 좀비 정리 (UI 닫기 경로도 동일하게 처리)
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const showAddModalRef = React.useRef(showAddModal);
  React.useEffect(() => { showAddModalRef.current = showAddModal; }, [showAddModal]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = (e) => {
      // 추가 팝업이 열려있으면 팝업만 닫기
      if (showAddModalRef.current) {
        setAddModalAnimating(false);
        setTimeout(() => setShowAddModal(false), 200);
      } else {
        onCloseRef.current?.();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // history 좀비 정리: 우리 state가 최상단이면 pop (UI 닫기 경로)
      if (window.history.state?.categoryEdit) {
        window.history.back();
      }
    };
  }, [isOpen]);  // ← onClose, showAddModal 의존 제거
  
  // 팝업이 열릴 때 히스토리 추가
  useEffect(() => {
    if (showAddModal) {
      window.history.pushState({ modal: 'addCategory' }, '');
    }
  }, [showAddModal]);
  
  
  // 키보드 높이 감지
  useEffect(() => {
    if (!showAddModal) return;
    
    const handleViewportChange = () => {
      if (window.visualViewport) {
        const keyboardH = window.innerHeight - window.visualViewport.height;
        setKeyboardHeight(keyboardH);
      }
    };
    
    // Visual Viewport API 사용
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      handleViewportChange(); // 초기값 설정
    }
    
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
      setKeyboardHeight(0);
    };
  }, [showAddModal]);

  // 화면 크기에 따른 스케일 조정
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    if (isVisible) {
      // 초기 스케일 설정만 수행
      scaleUI();
    }
  }, [isVisible]);

  // 스크롤바 계산 및 업데이트
  useEffect(() => {
    const updateScrollbar = () => {
      if (formContainerRef.current) {
        const scrollHeight = formContainerRef.current.scrollHeight;
        const clientHeight = formContainerRef.current.clientHeight;
        const scrollTop = formContainerRef.current.scrollTop;
        
        // 스크롤바가 필요한지 확인
        if (scrollHeight <= clientHeight) {
          setShowScrollbar(false);
          return;
        }
        
        setShowScrollbar(true);
        
        // 고정된 스크롤바 높이
        const fixedScrollbarHeight = 63; // 3배로 증가
        const trackHeight = clientHeight - 70; // bottom-actions 높이 제외
        const maxPosition = trackHeight - fixedScrollbarHeight;
        
        // 스크롤 가능한 높이와 현재 스크롤 위치로 비율 계산
        const scrollableHeight = scrollHeight - clientHeight;
        const scrollPercentage = scrollTop / scrollableHeight;
        
        // 스크롤바 위치 계산
        const scrollbarPosition = scrollPercentage * maxPosition;
        
        setScrollbarTop(scrollbarPosition);
      }
    };

    const handleScroll = (e) => {
      e.stopPropagation(); // 스크롤 이벤트 전파 방지
      if (!isScrollbarDragging) {
        updateScrollbar();
      }
    };

    const formContainer = formContainerRef.current;
    if (formContainer && isVisible) {
      updateScrollbar();
      formContainer.addEventListener('scroll', handleScroll, { passive: false });
      
      // 터치 이벤트도 제어
      const handleTouchMove = (e) => {
        // 드래그 중인 경우에만 스크롤 방지
        if (isDraggingItem) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      
      return () => {
        formContainer.removeEventListener('scroll', handleScroll);
        document.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isVisible, isScrollbarDragging, itemDragStartY, isDraggingItem, items]);

  // 아이템 드래그 이벤트 리스너
  useEffect(() => {
    if (isDraggingItem) {
      const handleMove = (e) => handleItemDragMove(e);
      const handleEnd = (e) => handleItemDragEnd(e);
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDraggingItem, draggedItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // 스크롤바 드래그 핸들러
  const handleScrollbarMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsScrollbarDragging(true);
    const startY = e.clientY || e.touches[0].clientY;
    const startTop = scrollbarTop;
    
    const handleScrollbarMove = (e) => {
      const currentY = e.clientY || e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      if (formContainerRef.current) {
        const clientHeight = formContainerRef.current.clientHeight;
        const scrollHeight = formContainerRef.current.scrollHeight;
        const trackHeight = clientHeight - 70; // bottom-actions 높이 제외
        const fixedScrollbarHeight = 63; // 3배로 증가 // 고정된 스크롤바 높이
        const maxPosition = trackHeight - fixedScrollbarHeight;
        
        // 새 스크롤바 위치 계산
        let newTop = startTop + deltaY;
        newTop = Math.max(0, Math.min(newTop, maxPosition));
        setScrollbarTop(newTop);
        
        // 스크롤 위치 계산
        const scrollPercentage = newTop / maxPosition;
        const newScrollTop = scrollPercentage * (scrollHeight - clientHeight);
        formContainerRef.current.scrollTop = newScrollTop;
      }
    };
    
    const handleScrollbarUp = () => {
      setIsScrollbarDragging(false);
      document.removeEventListener('mousemove', handleScrollbarMove);
      document.removeEventListener('mouseup', handleScrollbarUp);
      document.removeEventListener('touchmove', handleScrollbarMove);
      document.removeEventListener('touchend', handleScrollbarUp);
    };
    
    document.addEventListener('mousemove', handleScrollbarMove);
    document.addEventListener('mouseup', handleScrollbarUp);
    document.addEventListener('touchmove', handleScrollbarMove, { passive: false });
    document.addEventListener('touchend', handleScrollbarUp);
  };

  const handleSaveClick = async () => {
    // 이미 저장 중이면 중복 실행 방지
    if (isSaving) {
      console.log('이미 저장 중입니다.');
      return;
    }

    // 모드2: 커스텀 카테고리 — 추가/삭제/이름변경/순서를 일괄 저장
    if (isCustom) {
      // 빈 이름 검증
      const emptyItems = items.filter(item => !item.name || !item.name.trim());
      if (emptyItems.length > 0) {
        setInvalidItems(emptyItems.map(item => item.id));
        if (navigator.vibrate) navigator.vibrate(100);
        const firstEmptyIndex = items.findIndex(item => !item.name || !item.name.trim());
        const emptyElement = itemRefs.current[firstEmptyIndex];
        if (emptyElement) emptyElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      // 삭제된 카테고리가 있을 때만 확인창 — 수정·추가만 했을 땐 안 띄움
      const removedCount = originalItems.filter(o => !items.some(c => c.cid === o.cid)).length;
      if (removedCount > 0) {
        const ok = window.confirm(
          `삭제된 카테고리 ${removedCount}개가 있습니다.\n` +
          `저장하면 해당 카테고리에 배치된 메뉴 구성도 함께 삭제됩니다.\n계속하시겠습니까?`
        );
        if (!ok) return;
      }
      setIsSaving(true);
      try {
        await handleCustomSave();
      } finally {
        setIsSaving(false);
      }
      onClose();
      return;
    }

    // 빈 필드가 있는지 확인
    const emptyItems = items.filter(item => !item.name || !item.name.trim());

    if (emptyItems.length > 0) {
      // 빈 필드들에 에러 표시
      setInvalidItems(emptyItems.map(item => item.id));

      // 진동 피드백
      if (navigator.vibrate) {
        navigator.vibrate(100);
      }

      // 첫 번째 빈 필드로 스크롤
      const firstEmptyIndex = items.findIndex(item => !item.name || !item.name.trim());
      if (firstEmptyIndex !== -1 && formContainerRef.current) {
        const emptyElement = itemRefs.current[firstEmptyIndex];
        if (emptyElement) {
          emptyElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    // 현재 편집 중인 항목 저장
    if (editingItemId) {
      const index = items.findIndex(item => item.id === editingItemId);
      if (index !== -1) {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], name: editingText.trim() };
        setItems(newItems);
      }
    }

    // 실제로 변경된 항목만 찾기
    const changedItems = items.filter((item, index) => {
      const originalItem = originalItems[index];
      return item.isNew || // 새 항목
             !originalItem || // 원본이 없는 경우
             item.name !== originalItem.name; // 이름이 변경된 경우
    });

    // 순서 변경 여부 확인
    const orderChanged = items.length !== originalItems.length ||
      items.some((item, index) => {
        const originalItem = originalItems[index];
        return !originalItem || item.originalKey !== originalItem.originalKey;
      });

    // 숨김 상태 변경 여부 확인
    const visibilityChanged = items.some((item, index) => {
      const originalItem = originalItems[index];
      return originalItem && (item.is_hidden || false) !== (originalItem.is_hidden || false);
    });

    console.log('=== 카테고리 저장 시작 ===');
    console.log('변경된 항목 수:', changedItems.length);
    console.log('변경된 항목:', changedItems);
    console.log('순서 변경 여부:', orderChanged);
    console.log('숨김 변경 여부:', visibilityChanged);

    // 변경사항이 없고 순서도 변경되지 않았으면 바로 닫기
    if (changedItems.length === 0 && !orderChanged && !visibilityChanged) {
      console.log('변경사항이 없습니다.');
      onClose();
      return;
    }

    // 저장 시작
    setIsSaving(true);

    try {
      // 변경사항 저장
      const finalCategories = items.map(item => ({
        name: item.originalKey || item.name,
        is_hidden: item.is_hidden || false
      }));
      onReorder(finalCategories);

      // 변경된 항목만 업데이트 - Promise.all로 병렬 처리
      const updatePromises = changedItems.map(async (item) => {
      console.log('처리 중인 item:', item);
      console.log('item.originalKey:', item.originalKey);
      console.log('item.name:', item.name);
      console.log('item.isNew:', item.isNew);

      if (!item.name || !item.name.trim()) {
        console.log('빈 항목 건너뜀:', item);
        return; // 빈 항목 건너뛰기
      }

      // originalKey 체크와 처리
      if (item.isNew) {
        // 새 카테고리인 경우
        const newKey = item.name.trim();
        console.log('새 카테고리 추가:', newKey);

        // categories 배열에 추가
        if (!finalCategories.includes(newKey)) {
          finalCategories.push(newKey);
        }

        // 새 카테고리는 DB에 저장할 API가 필요함
        console.log('새 카테고리는 로컬에만 저장 (DB 저장 API 추가 필요)');
      } else if (item.originalKey && item.originalKey.trim()) {
        // 기존 카테고리 수정
        console.log('기존 카테고리 수정 시도:', item.originalKey, selectedLanguage, item.name);
        if (onUpdateCategoryTranslation) {
          try {
            await onUpdateCategoryTranslation(item.originalKey, selectedLanguage, item.name);
            console.log('기존 카테고리 수정 성공');
          } catch (error) {
            console.error('카테고리 번역 업데이트 실패:', error);
          }
        }
      } else {
        console.log('originalKey가 없거나 빈 문자열인 항목 건너뜀:', item);
        console.log('- item.id:', item.id);
        console.log('- item.originalKey:', `"${item.originalKey}"`);
        console.log('- item.isNew:', item.isNew);
      }
      });

      // 모든 업데이트가 완료될 때까지 대기
      await Promise.all(updatePromises);
      console.log('=== 카테고리 저장 완료 ===');

      // 성공적으로 완료되면 originalItems 업데이트
      setOriginalItems([...items]);

      // 닫기
      onClose();

      // 닫힌 후 토스트 팝업 표시 (callSettings와 동일 패턴)
      setTimeout(() => {
        if (onShowToast) {
          onShowToast(`<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>저장되었습니다`);
        }
      }, 300);
    } catch (error) {
      console.error('카테고리 저장 중 오류:', error);
    } finally {
      // 저장 완료
      setIsSaving(false);
    }
  };

  /* +추가 버튼 관련 함수 - 주석 처리
  const handleAddClick = () => {
    // 새 카테고리를 바로 인라인으로 추가
    const newId = `category-new-${Date.now()}`;
    const newItem = {
      id: newId,
      name: '',
      originalKey: '',
      isNew: true
    };
    const newItems = [...items, newItem];
    setItems(newItems);

    // 새로 추가된 아이템으로 스크롤
    if (formContainerRef.current) {
      setTimeout(() => {
        formContainerRef.current.scrollTop = formContainerRef.current.scrollHeight;
      }, 100);
    }

    // 바로 편집 모드로 전환
    setTimeout(() => {
      setEditingItemId(newId);
      setEditingText('');
      // input에 포커스
      setTimeout(() => {
        const input = document.querySelector(`#edit-input-${newId}`);
        if (input) {
          input.focus();
          input.select();
        }
      }, 50);
    }, 150);
  };
  */
  
  /* 모달 관련 함수 - 주석 처리
  const handleModalAddCategory = () => {
    if (!newCategoryName.trim()) {
      setInputError(true);
      setAnimationKey(prev => prev + 1);
      // 에러 상태 유지 (setTimeout 제거)
      return;
    }

    // 새 카테고리 추가
    const newCategoryKey = newCategoryName.trim();
    const newId = `category-new-${Date.now()}`;
    const newItem = {
      id: newId,
      name: newCategoryName.trim(),
      originalKey: newCategoryKey
    };
    const newItems = [...items, newItem];
    setItems(newItems);

    // categories 배열에도 추가
    const newCategories = [...categories, newCategoryKey];
    onReorder(newCategories);

    // 새 카테고리의 번역 정보도 추가
    const updatedTranslations = {
      ...categoryTranslations,
      [newCategoryKey]: {
        ko: newCategoryName.trim(),
        en: newCategoryName.trim(),
        ja: newCategoryName.trim(),
        zh: newCategoryName.trim()
      }
    };
    if (onUpdateCategoryTranslation) {
      onUpdateCategoryTranslation(updatedTranslations);
    }

    // 버튼 애니메이션 후 모달 닫기
    setTimeout(() => {
      setAddModalAnimating(false);
      setTimeout(() => setShowAddModal(false), 200);
      setNewCategoryName('');
    }, 200);

    // 새로 추가된 아이템으로 스크롤
    if (formContainerRef.current) {
      setTimeout(() => {
        formContainerRef.current.scrollTop = formContainerRef.current.scrollHeight;
      }, 400);
    }
  };

  const handleModalCancel = () => {
    // 버튼 애니메이션 후 모달 닫기
    setTimeout(() => {
      setAddModalAnimating(false);
      setTimeout(() => {
        setShowAddModal(false);
        setInputError(false);
      }, 200);
      setNewCategoryName('');
    }, 200);
  };
  */

  const handleEditClick = (item) => { // eslint-disable-line no-unused-vars
    // 한국어일 때는 편집 모드 진입 차단
    if (selectedLanguage === 'ko') {
      console.log('한국어 카테고리명은 편집할 수 없습니다.');
      return;
    }

    // 인라인 편집 모드로 전환
    setEditingItemId(item.id);
    setEditingText(item.name);
    // input에 포커스를 주기 위해 약간의 지연
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
        // 커서를 텍스트 끝으로 이동
        editInputRef.current.setSelectionRange(item.name.length, item.name.length);
      }
    }, 50);
  };

  const handleEditComplete = async () => {
    if (editingItemId && editingText.trim()) {
      const index = items.findIndex(item => item.id === editingItemId);
      if (index !== -1) {
        if (isNewItem) {
          // 새 카테고리인 경우 categories에 추가
          const newCategoryKey = editingText.trim();
          const newCategories = [...categories, newCategoryKey];
          onReorder(newCategories);

          // 새 카테고리의 번역도 설정
          if (onUpdateCategoryTranslation) {
            await onUpdateCategoryTranslation(newCategoryKey, selectedLanguage, editingText.trim());
          }
        } else if (editingText !== items[index].name) {
          // 기존 카테고리 수정
          const originalKey = items[index].originalKey;

          // 먼저 로컬 상태를 업데이트
          const newItems = [...items];
          newItems[index] = { ...newItems[index], name: editingText.trim() };
          setItems(newItems);

          // API 호출하여 DB 업데이트
          if (onUpdateCategoryTranslation) {
            await onUpdateCategoryTranslation(originalKey, selectedLanguage, editingText.trim());
          }
        }
      }
    } else if (isNewItem && editingItemId) {
      // 빈 텍스트로 완료하면 새 아이템 제거
      const newItems = items.filter(item => item.id !== editingItemId);
      setItems(newItems);
    }
    setEditingItemId(null);
    setEditingText('');
    setIsNewItem(false);
  };

  const handleEditCancel = () => {
    if (isNewItem && editingItemId) {
      // 새 아이템을 취소하면 제거
      const newItems = items.filter(item => item.id !== editingItemId);
      setItems(newItems);
    }
    setEditingItemId(null);
    setEditingText('');
    setIsNewItem(false);
  };

  const handleEditKeyDown = (e) => { // eslint-disable-line no-unused-vars
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditComplete();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    }
  };
  
  // input에서 포커스가 빠져나갈 때 호출되는 함수 수정
  const handleEditBlur = () => { // eslint-disable-line no-unused-vars
    // 완료 버튼을 클릭한 경우가 아니면 취소
    // setTimeout을 사용하여 클릭 이벤트가 먼저 처리되도록 함
    setTimeout(() => {
      if (document.activeElement?.className?.includes('complete')) {
        return;
      }
      // 빈 텍스트인 새 아이템은 취소
      if (isNewItem && !editingText.trim()) {
        handleEditCancel();
      }
    }, 100);
  };

  const handleDeleteClick = (item) => { // eslint-disable-line no-unused-vars
    // 이미 삭제 중이면 무시
    if (deletingItemId) return;

    // 삭제 애니메이션 시작
    setDeletingItemId(item.id);

    // 애니메이션 후 UI에서만 제거 (DB 업데이트는 저장 버튼 클릭 시)
    setTimeout(() => {
      const newItems = items.filter(i => i.id !== item.id);
      setItems(newItems);
      setDeletingItemId(null);
    }, 300); // 애니메이션 시간과 동일
  };


  // 드래그 관련 함수들
  const handleItemDragStart = (e, index) => {
    // 드래그 시작 시에만 기본 동작 방지
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setItemDragStartY(clientY);
    setDraggedItem(index);
    setOriginalIndex(index);
    setIsDraggingItem(true);
    // 초기 스크롤 위치 저장
    initialScrollTop.current = formContainerRef.current?.scrollTop || 0;
    accumulatedScroll.current = 0;
    // 드래그가 시작되면 스크롤 방지
    e.preventDefault();
    e.stopPropagation();
  };

  const handleItemDragMove = (e) => {
    if (!isDraggingItem || draggedItem === null || originalIndex === null) return;
    
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const draggedElement = itemRefs.current[originalIndex];
    if (!draggedElement) return;

    // 현재 스크롤 위치와 초기 스크롤 위치의 차이 계산
    const currentScrollTop = formContainerRef.current?.scrollTop || 0;
    const scrollDelta = currentScrollTop - initialScrollTop.current;
    
    // 현재 드래그 위치 계산 (스크롤 변화량 보정)
    const deltaY = clientY - itemDragStartY + scrollDelta;
    
    // 드래그 중인 아이템 이동
    draggedElement.style.transform = `translateY(${deltaY}px)`;
    draggedElement.style.zIndex = '1000';
    draggedElement.style.position = 'relative';

    // 스크롤이 필요한 경우 자동 스크롤
    const containerRect = formContainerRef.current.getBoundingClientRect();
    const dragY = clientY - containerRect.top;
    
    // 자동 스크롤 함수
    const startAutoScroll = (direction, speed) => {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
      }
      
      const scroll = () => {
        if (!isDraggingItem || !formContainerRef.current) {
          cancelAnimationFrame(autoScrollRef.current);
          return;
        }
        
        const container = formContainerRef.current;
        const draggedElement = itemRefs.current[originalIndex];
        
        if (direction === 'up' && container.scrollTop > 0) {
          const oldScrollTop = container.scrollTop;
          container.scrollTop = Math.max(0, container.scrollTop - speed);
          const scrollChange = oldScrollTop - container.scrollTop;
          
          // 드래그 중인 요소의 위치도 함께 업데이트
          if (draggedElement) {
            const currentTransform = draggedElement.style.transform;
            const currentY = parseFloat(currentTransform.replace('translateY(', '').replace('px)', '')) || 0;
            draggedElement.style.transform = `translateY(${currentY - scrollChange}px)`;
          }
          
          autoScrollRef.current = requestAnimationFrame(scroll);
        } else if (direction === 'down' && container.scrollTop < container.scrollHeight - container.clientHeight) {
          const oldScrollTop = container.scrollTop;
          container.scrollTop = Math.min(
            container.scrollHeight - container.clientHeight,
            container.scrollTop + speed
          );
          const scrollChange = container.scrollTop - oldScrollTop;
          
          // 드래그 중인 요소의 위치도 함께 업데이트
          if (draggedElement) {
            const currentTransform = draggedElement.style.transform;
            const currentY = parseFloat(currentTransform.replace('translateY(', '').replace('px)', '')) || 0;
            draggedElement.style.transform = `translateY(${currentY + scrollChange}px)`;
          }
          
          autoScrollRef.current = requestAnimationFrame(scroll);
        }
      };
      
      autoScrollRef.current = requestAnimationFrame(scroll);
    };
    
    // 상단 근처에서 위로 스크롤
    if (dragY < 100) {
      const scrollSpeed = Math.max(2, Math.min(15, (100 - dragY) / 5)); // 거리에 따라 속도 조절
      startAutoScroll('up', scrollSpeed);
    } 
    // 하단 근처에서 아래로 스크롤
    else if (dragY > containerRect.height - 100) {
      const scrollSpeed = Math.max(2, Math.min(15, (dragY - (containerRect.height - 100)) / 5)); // 거리에 따라 속도 조절
      startAutoScroll('down', scrollSpeed);
    }
    // 중간 영역에서는 스크롤 중지
    else {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    }

    // 드래그 중인 아이템의 현재 위치 계산
    const draggedRect = draggedElement.getBoundingClientRect();
    const draggedMidY = draggedRect.top + draggedRect.height / 2;
    
    let newIndex = originalIndex;
    
    // 드래그 중인 아이템이 어느 위치에 있는지 확인
    for (let i = 0; i < items.length; i++) {
      if (i === originalIndex) continue;
      
      const targetElement = itemRefs.current[i];
      if (!targetElement) continue;
      
      const targetRect = targetElement.getBoundingClientRect();
      
      // 원본 위치보다 아래로 드래그하는 경우
      if (originalIndex < i) {
        // 드래그 중인 아이템의 중심이 대상 아이템의 중심을 넘어갔을 때
        if (draggedMidY > targetRect.top + targetRect.height / 2) {
          newIndex = i;
        }
      } 
      // 원본 위치보다 위로 드래그하는 경우
      else {
        // 드래그 중인 아이템의 중심이 대상 아이템의 중심보다 위에 있을 때
        if (draggedMidY < targetRect.top + targetRect.height / 2) {
          newIndex = i;
          break;
        }
      }
    }
    
    // 위치가 변경되었을 때만 업데이트
    if (newIndex !== draggedItem) {
      setDraggedItem(newIndex);
      
      // 다른 아이템들 애니메이션
      const draggedElement = itemRefs.current[originalIndex];
      const itemHeight = draggedElement ? draggedElement.offsetHeight : 79;
      itemRefs.current.forEach((ref, index) => {
        if (ref && index !== originalIndex) {
          ref.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          
          let offset = 0;
          if (originalIndex < newIndex) {
            // 원본이 위에서 아래로
            if (index > originalIndex && index <= newIndex) {
              offset = -itemHeight;
            } else {
              offset = 0;
            }
          } else if (originalIndex > newIndex) {
            // 원본이 아래에서 위로
            if (index >= newIndex && index < originalIndex) {
              offset = itemHeight;
            } else {
              offset = 0;
            }
          }
          
          ref.style.transform = `translateY(${offset}px)`;
        }
      });
    }
  };

  const handleItemDragEnd = () => {
    if (!isDraggingItem || draggedItem === null || originalIndex === null) return;
    
    // 드래그 종료 시 진동 피드백
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }
    
    // 자동 스크롤 중지
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
    
    // 실제로 아이템 순서 변경
    if (originalIndex !== draggedItem) {
      const newItems = [...items];
      const [removed] = newItems.splice(originalIndex, 1);
      newItems.splice(draggedItem, 0, removed);
      setItems(newItems);

      // 모드1·모드2 공통: 저장 버튼 누를 때만 DB 반영 — 여기서는 로컬 items만 갱신
    }
    
    // 모든 요소의 스타일 초기화를 requestAnimationFrame으로 처리
    requestAnimationFrame(() => {
      // 먼저 모든 요소의 transition을 제거하고 원위치
      itemRefs.current.forEach((ref) => {
        if (ref) {
          ref.style.transition = 'none';
          ref.style.transform = '';
          ref.style.zIndex = '';
          ref.style.position = '';
        }
      });
      
      // 다음 프레임에서 transition 복원
      requestAnimationFrame(() => {
        itemRefs.current.forEach((ref) => {
          if (ref) {
            ref.style.transition = '';
          }
        });
      });
    });
    
    // 상태 초기화
    setDraggedItem(null);
    setOriginalIndex(null);
    setIsDraggingItem(false);
  };


  if (!isVisible) return null;

  return (
    <div className={`category-edit-overlay ${isAnimating ? 'open' : ''}`} onClick={onClose}>
      <div 
        ref={bottomSheetRef}
        className={`category-edit-sheet ${isAnimating ? 'open' : ''} ${isDragging ? 'dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          ref={wrapperRef}
          className="category-edit-wrapper"
          style={{ 
            transform: `translateY(${currentY}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isDragging && currentY > 50 ? 0.95 : 1
          }}
        >
        {/* 드래그 핸들 영역 */}
        <div 
          className="drag-handle-area"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="drag-handle" />
        </div>

        {/* 헤더 섹션 */}
        <div className="header-section">
          <h2 className="header-title">카테고리 설정</h2>
          <div className="sub-text-container">
            {selectedLanguage === 'ko' ? (
              <>
                <img
                  className="header-drag-icon"
                  src={adminHamburgerIcon}
                  alt="drag"
                  width="15"
                  height="11"
                />
                <span className="sub-text">위 아래로 순서를 바꿀 수 있어요!</span>
              </>
            ) : selectedLanguage === 'en' ? (
              <>
                <img
                  className="header-flag-icon"
                  src={languageEnIcon}
                  alt="English"
                  width="20"
                  height="20"
                />
                <span className="sub-text">카테고리명을 영어로 번역해주세요!</span>
              </>
            ) : selectedLanguage === 'zh' ? (
              <>
                <img
                  className="header-flag-icon"
                  src={languageCnIcon}
                  alt="Chinese"
                  width="20"
                  height="20"
                />
                <span className="sub-text">카테고리명을 중국어로 번역해주세요!</span>
              </>
            ) : selectedLanguage === 'ja' ? (
              <>
                <img
                  className="header-flag-icon"
                  src={languageJaIcon}
                  alt="Japanese"
                  width="20"
                  height="20"
                />
                <span className="sub-text">카테고리명을 일본어로 번역해주세요!</span>
              </>
            ) : null}
          </div>
        </div>

        {/* 카테고리 리스트 컨테이너 */}
        <div className="category-list-container">
          {/* 헤더 */}
          <div className="list-header">
            <span className="total-count">총 {items.length}개</span>
          </div>
          
          {/* 카테고리 리스트 */}
          <div className="category-list" ref={formContainerRef}>
            {items.map((item, index) => (
              <div 
                key={item.id} 
                className={`category-bar ${isCustom ? 'custom-mode' : ''} ${originalIndex === index && isDraggingItem ? 'dragging' : ''} ${deletingItemId === item.id ? 'deleting' : ''}${item.is_hidden ? ' hidden-bar' : ''}`}
                ref={el => itemRefs.current[index] = el}
              >
                {/* 햄버거 아이콘 - 한국어 또는 모드2(커스텀)일 때 드래그 가능 */}
                {(selectedLanguage === 'ko' || isCustom) && (
                  <div
                    className="drag-icon-wrapper"
                    onMouseDown={(e) => {
                      if ('vibrate' in navigator) {
                        navigator.vibrate(30);
                      }
                      handleItemDragStart(e, index);
                    }}
                    onTouchStart={(e) => {
                      if ('vibrate' in navigator) {
                        navigator.vibrate(30);
                      }
                      handleItemDragStart(e, index);
                    }}
                  >
                    <img
                      className="drag-icon"
                      src={adminHamburgerIcon}
                      alt="drag"
                      width="15"
                      height="11"
                    />
                  </div>
                )}
                
                {/* 카테고리 이름 - 호출 설정과 동일한 스타일 */}
                <div className="category-display-text">
                  <input
                    type="text"
                    className={`category-edit-input ${invalidItems.includes(item.id) ? 'invalid' : ''} ${(selectedLanguage === 'ko' && !isCustom) ? 'readonly' : ''} ${item.is_hidden ? 'hidden-item' : ''}`}
                    value={editingItemId === item.id ? editingText : item.name}
                    readOnly={selectedLanguage === 'ko' && !isCustom}
                    disabled={selectedLanguage === 'ko' && !isCustom}
                    onChange={(e) => {
                      // 한국어일 때는 변경 차단 (모드2 커스텀은 ko도 편집 가능)
                      if (selectedLanguage === 'ko' && !isCustom) {
                        e.preventDefault();
                        return;
                      }

                      const newValue = e.target.value;

                      // items 배열 직접 수정
                      const updatedItems = [...items];
                      const itemIndex = updatedItems.findIndex(i => i.id === item.id);
                      if (itemIndex !== -1) {
                        // 새 카테고리인 경우 입력값을 originalKey로 설정
                        if (updatedItems[itemIndex].isNew && newValue.trim()) {
                          updatedItems[itemIndex] = {
                            ...updatedItems[itemIndex],
                            name: newValue,
                            originalKey: newValue.trim()
                          };
                        } else {
                          // 기존 카테고리는 originalKey 보존
                          updatedItems[itemIndex] = {
                            ...updatedItems[itemIndex],
                            name: newValue,
                            originalKey: updatedItems[itemIndex].originalKey
                          };
                        }
                        setItems(updatedItems);
                      }

                      if (editingItemId === item.id) {
                        setEditingText(newValue);
                      } else {
                        // 직접 편집 시작
                        setEditingItemId(item.id);
                        setEditingText(newValue);
                        setIsNewItem(item.isNew || false);
                      }

                      // 입력 시 에러 상태 해제
                      if (invalidItems.includes(item.id)) {
                        setInvalidItems(invalidItems.filter(id => id !== item.id));
                      }
                    }}
                    onFocus={() => {
                      // 한국어일 때는 편집 모드 진입 차단 (모드2 커스텀은 ko도 편집 가능)
                      if (selectedLanguage === 'ko' && !isCustom) {
                        return;
                      }
                      if (editingItemId !== item.id) {
                        setEditingItemId(item.id);
                        setEditingText(item.name);
                        setIsNewItem(item.isNew || false);  // isNew 속성 유지
                      }
                    }}
                    onBlur={() => {
                      // 모드2: 커스텀 카테고리는 blur 시 로컬 items에 이름 반영 (저장 시 일괄 PUT)
                      if (isCustom && editingItemId === item.id) {
                        if ((editingText || '').trim() && editingText.trim() !== item.originalKey) {
                          handleCustomRename(item.cid || item.id, editingText);
                        }
                        setEditingItemId(null);
                      }
                      // 모드1: 편집 완료 처리는 저장 버튼에서만
                    }}
                    placeholder="카테고리명을 입력해주세요"
                  />
                </div>

                {selectedLanguage === 'ko' && !isCustom && (
                  <div className="visibility-toggle" onClick={() => {
                    if (navigator.vibrate) navigator.vibrate(30);
                    setItems(prev => prev.map((it, i) =>
                      i === index ? { ...it, is_hidden: !it.is_hidden } : it
                    ));
                  }}>
                    {item.is_hidden ? (
                      <svg width="22" height="22" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.700195 6.18357C1.95358 2.97412 4.88024 0.700012 8.53385 0.700012C12.1875 0.700012 15.1141 2.97412 16.3675 6.18357C15.1141 9.39302 12.1875 11.6671 8.53385 11.6671C4.88024 11.6671 1.95358 9.39302 0.700195 6.18357Z" stroke="#888888" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="8.5332" cy="6.18359" r="2.24883" stroke="#888888" strokeWidth="1.4"/>
                        <path d="M2.26855 0.700012L14.8046 12.7" stroke="#888888" strokeWidth="1.4" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg width="22" height="22" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="8.5332" cy="6.18359" r="2.24883" stroke="#2F2F2F" strokeWidth="1.4"/>
                        <path d="M0.700195 6.18357C1.95358 2.97412 4.88024 0.700012 8.53385 0.700012C12.1875 0.700012 15.1141 2.97412 16.3675 6.18357C15.1141 9.39302 12.1875 11.6671 8.53385 11.6671C4.88024 11.6671 1.95358 9.39302 0.700195 6.18357Z" stroke="#2F2F2F" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                )}

                {/* 삭제 버튼 - 모드2(커스텀)에서만 노출 */}
                {isCustom && (
                  <button
                    className="category-action-btn delete-icon"
                    onClick={() => handleCustomDeleteCategory(item.cid, item.id)}
                  >
                    <img
                      src={adminDeleteIcon}
                      alt="삭제"
                      width="20"
                      height="20"
                    />
                  </button>
                )}
              </div>
            ))}

            {/* +추가 버튼 - 모드2(커스텀)에서만 노출 */}
            {isCustom && (
              <button
                className="add-category-bar"
                onClick={handleCustomAddCategory}
              >
                + 추가
              </button>
            )}

            {/* 하단 여백 */}
            <div style={{ height: '40px' }} />
          </div>
          
          {/* 커스텀 스크롤바 */}
          {showScrollbar && (
            <div className="custom-scrollbar-track">
              <div 
                ref={scrollbarRef}
                className="custom-scrollbar-thumb"
                style={{ 
                  top: `${scrollbarTop}px`,
                  height: '63px'
                }}
                onMouseDown={handleScrollbarMouseDown}
                onTouchStart={handleScrollbarMouseDown}
              />
            </div>
          )}
        </div>

        {/* 하단 버튼들 */}
        <div className="category-bottom-actions">
          <button className="category-action-button category-close-button" onClick={() => {
            setTimeout(() => {
              onClose();
            }, 50);
          }}>
            닫기
          </button>
          <button
            className="category-action-button category-add-button"
            onClick={handleSaveClick}
            disabled={isSaving}
            style={{ opacity: isSaving ? 0.7 : 1 }}
          >
            {isSaving ? '저장중...' : '저장'}
          </button>
        </div>
        </div>
      </div>

      <style jsx="true">{`
        .category-edit-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0);
          z-index: 1001;
          transition: background 0.2s ease;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }

        .category-edit-overlay.open {
          background: rgba(0, 0, 0, 0.5);
        }

        .category-edit-sheet {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          transform: translateY(100%);
          width: 100%;
          height: 90%;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .category-edit-sheet.open {
          transform: translateY(0);
        }
        
        .category-edit-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          background: white;
          border-radius: 24px 24px 0 0;
          overflow: hidden;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          transform: translateZ(0);
          overscroll-behavior: contain;
          touch-action: pan-y;
          contain: layout style;
        }
        
        @media screen and (min-width: 361px) {
          .category-edit-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .category-edit-wrapper {
            zoom: 1.5;
          }
        }
        
        @media screen and (max-width: 360px) {
          .category-edit-wrapper {
            width: 360px;
            zoom: calc(100vw / 360);
          }
        }

        .drag-handle-area {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 48px;
          cursor: grab;
          touch-action: none;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          z-index: 1;
          padding-top: 12px;
          box-sizing: border-box;
        }

        .drag-handle-area:active {
          cursor: grabbing;
        }

        .drag-handle {
          width: 52px;
          height: 4px;
          background: #E5E8EB;
          border-radius: 10px;
          transition: all 0.2s ease;
        }
        
        .category-edit-sheet.open .drag-handle {
          background: #E5E8EB;
        }
        
        .drag-handle-area:active .drag-handle {
          background: #C9CDD1;
          transform: scaleX(1.2);
        }

        .header-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          border-bottom: 1px solid #F3F5F7;
          padding-bottom: 24px;
          padding-top: 40px;
        }

        .category-bottom-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          background-image: url(${bottomNavBg});
          background-size: cover;
          background-position: center;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 16px;
          gap: 8px;
          pointer-events: none;
        }
        
        .category-bottom-actions .category-action-button {
          pointer-events: auto;
        }

        .category-action-button {
          width: 164px;
          height: 50px;
          border-radius: 10px;
          border: none;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.1s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .category-close-button {
          background: #F1F1F1;
          color: #959595;
        }

        .category-add-button {
          background: #FDCD27;
          color: #FFFFFF;
        }

        .category-close-button:active,
        .category-add-button:active {
          animation: buttonPress 0.1s ease-out;
        }

        .header-title {
          font-size: 18px;
          font-weight: 600;
          color: #2E2E2E;
          margin: 0;
          text-align: center;
        }
        
        .sub-text-container {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
        }
        
        .header-drag-icon {
          flex-shrink: 0;
        }

        .header-flag-icon {
          flex-shrink: 0;
          object-fit: cover;
        }

        .sub-text {
          font-size: 14px;
          font-weight: 500;
          color: #949494;
        }

        .category-list-container {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px 10px 20px;
        }
        
        .total-count {
          font-size: 14px;
          font-weight: 600;
          color: #888888;
          margin-left: 4px;
        }
        
        .category-list {
          height: 100%;
          padding: 0 0 100px 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          position: relative;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }
        
        .category-list::-webkit-scrollbar {
          display: none;
        }
        
        /* 커스텀 스크롤바 스타일 */
        .custom-scrollbar-track {
          position: absolute;
          right: 0px;
          top: 3px;
          bottom: 70px;
          width: 6px;
          z-index: 100;
        }
        
        .custom-scrollbar-thumb {
          position: absolute;
          right: 0;
          width: 6px;
          background: rgb(201, 201, 201);
          border-radius: 3px;
          cursor: grab;
          transition: background 0.2s ease;
        }
        
        .custom-scrollbar-thumb:hover {
          
        }
        
        .custom-scrollbar-thumb:active {
          cursor: grabbing;
          
        }

        .category-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: auto;
          padding: 10px 20px 10px 10px;
          position: relative;
          background: white;
          user-select: none;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .category-bar.custom-mode {
          padding-right: 10px;
        }
        
        .category-bar .visibility-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 22px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .category-bar.hidden-bar {
          opacity: 0.6;
          transition: none;
        }

        .category-bar.dragging {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
          transition: none;
          z-index: 1000;
        }
        
        .category-bar.deleting {
          animation: slideOutAndCollapse 0.3s ease-out forwards;
          transition: none;
        }
        
        @keyframes slideOutAndCollapse {
          0% {
            transform: translateX(0);
            opacity: 1;
            height: 72px;
            padding-top: 10px;
            padding-bottom: 10px;
          }
          50% {
            transform: translateX(-100%);
            opacity: 0;
            height: 72px;
            padding-top: 10px;
            padding-bottom: 10px;
          }
          100% {
            transform: translateX(-100%);
            opacity: 0;
            height: 0;
            padding-top: 0;
            padding-bottom: 0;
            margin: 0;
          }
        }
        
        .drag-icon-wrapper {
          flex-shrink: 0;
          cursor: grab;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
          padding: 30px 10px;
          margin: -15px -10px -15px 0;
          touch-action: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .drag-icon-wrapper:active {
          cursor: grabbing;
        }
        
        .drag-icon {
          pointer-events: none;
        }
        
        .category-display-text {
          flex: 1;
          margin-left: 20px;
        }

        .category-edit-input {
          width: 100%;
          height: 52px;
          padding: 14px 16px;
          border: 1.2px solid #E8E8E8;
          border-radius: 8px;
          outline: none;
          background: transparent;
          font-size: 16px;
          font-weight: 600; /* semibold */
          color: #282828;
          font-family: inherit;
          caret-color: #FDCD27;
          transition: border-color 0.2s ease;
        }

        .category-edit-input:focus {
          border-color: #FDCD27;
        }

        .category-edit-input::placeholder {
          color: #CBCBCB;
          font-weight: 500; /* medium */
          font-size: 17px;
        }

        .category-edit-input.invalid {
          border-color: #F41717;
          animation: shake 0.3s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }

        .category-edit-input.readonly {
          background-color: #FAFAFB;
          color: #282828;
          cursor: not-allowed;
          border-color: #E8E8E8;
        }

        .category-edit-input.readonly:focus {
          border-color: #E8E8E8;
        }

        .category-edit-input.hidden-item {
          color: #AAAAAA;
          text-decoration: line-through;
          background-color: #F3F3F3;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }

        .add-category-bar {
          width: calc(100% - 40px);
          height: 43px;
          background: #FFFFFF;
          border: none;
          font-size: 16px;
          font-weight: bold;
          color: #959595;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 6px 20px 0 20px;
          margin: 0 20px 10px 20px;
          transition: background-color 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .add-category-bar .ripple {
          position: absolute;
          border-radius: 50%;
          background: rgba(253, 205, 39, 0.4);
          transform: scale(0);
          animation: ripple-animation 0.6s ease-out;
          pointer-events: none;
        }

        @keyframes ripple-animation {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }

        .edit-icon {
          width: 19px;
          height: 19px;
          cursor: pointer;
        }
        
        .category-action-btn {
          width: 49px;
          height: 29px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          margin-left: auto;
        }
        
        .category-action-btn.edit {
          background: #F1F1F1;
          color: #666666;
        }
        
        .category-action-btn.delete-icon {
          background: transparent;
          border: none;
          padding: 0;
          width: auto;
          height: auto;
          margin: 0 6px 0 16px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          cursor: pointer;
        }

        .category-action-btn.delete-icon:active {
          opacity: 0.6;
        }
        
        .category-action-btn.complete {
          background: #FDCD27;
          color: #FFFFFF;
          font-weight: 600;
        }
        
        .category-action-btn.complete:active {
          animation: buttonPress 0.1s ease-out;
        }
        
        .category-action-btn.edit:active {
          animation: buttonPress 0.1s ease-out;
        }

        
        @keyframes buttonPress {
          0% {
            transform: scale(1);
          }
          40% {
            transform: scale(0.92);
          }
          100% {
            transform: scale(1);
          }
        }
        
        /* 팝업 모달 스타일 */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: ${keyboardHeight > 0 ? `calc(100% - ${keyboardHeight}px)` : '100%'};
          background-color: rgba(0, 0, 0, 0);
          z-index: 10001;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: background-color 0.2s ease, height 0.2s ease;
        }
        
        .modal-wrapper {
          transform-origin: center;
        }
        
        .modal-overlay.show {
          background-color: rgba(0, 0, 0, 0.5);
        }
        
        .modal-container {
          width: 320px;
          background-color: #FFFFFF;
          border-radius: 16px;
          padding: 32px 26px 28px 26px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
        }
        
        .modal-container.show {
          opacity: 1;
          transform: translateY(${keyboardHeight > 0 ? '50px' : '0'});
        }
        
        .modal-title {
          font-size: 20px;
          font-weight: bold;
          line-height: 28px;
          color: #000000;
          margin: 0;
        }
        
        .modal-subtitle {
          font-size: 12px;
          font-weight: 500;
          color: #888888;
          margin-top: 20px;
          padding: 0 2px;
          line-height: 14px;
        }
        
        .modal-input {
          width: 268px;
          height: 51px;
          background-color: #FFFFFF;
          border: 1px solid #E8E8E8;
          border-radius: 6px;
          padding: 0 12px;
          font-size: 16px;
          font-weight: 500;
          margin-top: 8px;
          outline: none;
          caret-color: #FDCD27;
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }
        
        .modal-input.has-value:not(.focused) {
          background-color: #FAFAFB;
          border-color: #E8E8E8;
        }
        
        .modal-input::placeholder {
          color: #CBCBCB;
        }
        
        .modal-input.focused {
          border-color: #FDCD27;
          background-color: #FFFFFF;
        }
        
        .modal-input.error {
          border-color: #FF4444 !important;
          border-width: 1px !important;
          animation: shake 0.4s ease-in-out;
        }
        
        .modal-input.error:focus {
          border-color: #FDCD27 !important;
          border-width: 1px !important;
        }
        
        @keyframes shake {
          0% { transform: translateX(0); }
          12.5% { transform: translateX(-1px); }
          25% { transform: translateX(1px); }
          37.5% { transform: translateX(-1px); }
          50% { transform: translateX(1px); }
          62.5% { transform: translateX(-1px); }
          75% { transform: translateX(1px); }
          87.5% { transform: translateX(-1px); }
          100% { transform: translateX(0); }
        }
        
        .modal-buttons {
          display: flex;
          gap: 8px;
          margin-top: 24px;
        }
        
        .modal-button {
          width: 130px;
          height: 48px;
          border-radius: 8px;
          border: none;
          font-size: 15px;
          cursor: pointer;
          transition: all 0.1s ease;
        }
        
        .modal-button:active {
          animation: buttonPress 0.1s ease-out;
        }
        
        .modal-button-cancel {
          background-color: #F1F1F1;
          color: #959595;
          font-weight: bold;
        }
        
        .modal-button-add {
          background-color: #FDCD27;
          color: #FFFFFF;
          font-weight: 900;
        }
        
        /* 삭제 확인 팝업 스타일 */
        .delete-confirm-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0);
          z-index: 20000;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding-top: 250px;
          transition: background-color 0.2s ease;
        }
        
        .delete-confirm-overlay.show {
          background-color: rgba(0, 0, 0, 0.5);
        }
        
        .delete-confirm-popup {
          width: 320px;
          background: #FFFFFF;
          border-radius: 16px;
          padding: 30px 26px 24px 26px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
        }
        
        .delete-confirm-popup.show {
          opacity: 1;
          transform: translateY(0);
        }
        
        .delete-confirm-text {
          font-size: 20px;
          font-weight: bold;
          line-height: 28px;
          text-align: left;
          color: #2E2E2E;
          margin-bottom: 24px;
        }
        
        .delete-confirm-buttons {
          display: flex;
          gap: 8px;
        }
        
        .delete-cancel-button,
        .delete-confirm-button {
          flex: 1;
          height: 48px;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.1s ease;
        }
        
        .delete-cancel-button {
          background: #F1F1F1;
          color: #959595;
        }
        
        .delete-confirm-button {
          background: #FFDEDE;
          color: #FF3838;
        }
        
        .delete-cancel-button:active,
        .delete-confirm-button:active {
          transform: scale(0.95);
        }

      `}</style>
      
      {/* 팝업 모달 - 주석 처리 */}
      {/* {showAddModal && (
        <div className={`modal-overlay ${addModalAnimating ? 'show' : ''}`} onClick={handleModalCancel}>
          <div
            className="modal-wrapper"
            style={{
              zoom: window.innerWidth > 540 ? 1.5 : Math.min(window.innerWidth / 360, 1.5)
            }}
          >
            <div className={`modal-container ${addModalAnimating ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">새 카테고리 추가</h2>
            <input
              key={`modal-input-${animationKey}`}
              type="text"
              className={`modal-input ${inputError ? 'error' : ''} ${inputFocused ? 'focused' : ''} ${newCategoryName ? 'has-value' : ''}`}
              placeholder="카테고리명을 입력해주세요"
              value={newCategoryName}
              onChange={(e) => {
                setNewCategoryName(e.target.value);
                if (inputError) {
                  setInputError(false);
                }
              }}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleModalAddCategory();
                }
              }}
              autoFocus={!inputError}
            />
            <div className="modal-buttons">
              <button
                className="modal-button modal-button-cancel"
                onClick={handleModalCancel}
              >
                취소
              </button>
              <button
                className="modal-button modal-button-add"
                onClick={handleModalAddCategory}
              >
                추가
              </button>
            </div>
          </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default CategoryEditBottomSheet; 